import React from "react";
import { FormProvider } from "react-hook-form";
import { Button, Callout, Form } from "@sscs/ui";
import { LockIcon as MUILockOutlinedIcon } from "@sscs/ui/icons";
import { getClassName } from "@sscs/utils";
import { useReportPasswordProtection } from "../../hooks";
import styles from "./reportPasswordForm.module.scss";

type ReportPasswordFormProps = {
  token: string;
  onSuccessfulLogin: () => void;
};

const ReportPasswordForm = ({
  token,
  onSuccessfulLogin,
}: ReportPasswordFormProps): React.JSX.Element => {
  const { isAuthError, errors, methods, onSubmit } =
    useReportPasswordProtection(token, onSuccessfulLogin);

  const passwordError = Object.keys(errors).includes("password")
    ? (errors.password?.message ?? "")
    : "";
  const hasError = !!passwordError || isAuthError;
  const tooManyAttempts =
    isAuthError && "status" in errors && errors.status === 403;

  return (
    <div className={styles["form-container"]}>
      <div className={styles["info-content"]}>
        <MUILockOutlinedIcon className={styles["lock-icon"]} />
        <h1 className={styles["title"]}>Password required</h1>
        <div className={styles["description"]}>
          <p>
            You{"'"}re trying to access a password-protected{" "}
            <b>Spectra Assure report</b>.
          </p>
          {!tooManyAttempts && (
            <p>To proceed, enter the password provided with the report link:</p>
          )}
        </div>
      </div>
      <FormProvider {...methods}>
        <Form.Form onSubmit={onSubmit} className={styles["form"]}>
          {!tooManyAttempts ? (
            <>
              <Form.TextField
                label="Password"
                className={getClassName({
                  [styles.error]: hasError,
                })}
                placeholder="Password"
                name="password"
                type="password"
                errors={errors}
                isRequired
              />
              {isAuthError && (
                <Callout type="error" title="Invalid password">
                  Wrong password. Make sure it matches the password you received
                  with the report link, and try again.
                </Callout>
              )}
              <Button isFullWidth type="submit" color="primary" size="large">
                Submit
              </Button>
            </>
          ) : (
            <Callout type="error" title="Too many failed password attempts">
              Make sure you have the correct password for this report, and try
              again later.
            </Callout>
          )}
        </Form.Form>
      </FormProvider>
    </div>
  );
};

export default ReportPasswordForm;
